import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '../contexts/ThemeContext';
import { workService } from '../services/workService';
import { WorkStatusBadge } from '../components/work/WorkStatusBadge';
import { PriorityChip } from '../components/work/PriorityChip';
import { DueDateLabel } from '../components/work/DueDateLabel';
import UserBadge from '../components/common/UserBadge';

// TODO: Full implementation in A2 Task 6 — metadata grid, time tracking, entity refs
const TaskDetail: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const { darkMode } = useTheme();
  const { data: task, isLoading } = useQuery(['task', taskId], () => workService.getTask(taskId!), { enabled: !!taskId });

  if (isLoading) return <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full" /></div>;
  if (!task) return <div className="py-20 text-center text-gray-400">Task not found</div>;

  return (
    <div className="space-y-6">
      {/* Identity strip */}
      <div className={`border-l-4 border-l-violet-500 rounded-r-lg p-6 ${darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
        <div className="flex-1">
          <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{task.title}</h1>
          {task.description && <p className={`mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{task.description}</p>}
          <div className="flex items-center gap-3 mt-3">
            <WorkStatusBadge status={task.status} size="md" />
            <PriorityChip priority={task.priority} />
            <DueDateLabel date={task.due_date} />
            <UserBadge owner={task.owner} size="sm" />
          </div>
        </div>
      </div>

      {/* Metadata grid */}
      <div className={`grid grid-cols-2 md:grid-cols-4 gap-4`}>
        {[
          { label: 'Assignee', value: task.assignee_id || '—' },
          { label: 'Scheduled', value: task.scheduled_at ? new Date(task.scheduled_at).toLocaleDateString() : '—' },
          { label: 'Estimated', value: task.estimated_minutes ? `${task.estimated_minutes}m` : '—' },
          { label: 'Actual', value: task.actual_minutes ? `${task.actual_minutes}m` : '—' },
        ].map(m => (
          <div key={m.label} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{m.label}</div>
            <div className={`text-sm font-medium mt-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{m.value}</div>
          </div>
        ))}
      </div>

      {/* Entity refs */}
      {task.entity_refs && task.entity_refs.length > 0 && (
        <div>
          <h3 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Linked Entities</h3>
          <div className="flex flex-wrap gap-2">
            {task.entity_refs.map(ref => (
              <span key={ref.id} className={`px-2 py-1 rounded text-xs ${darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                {ref.entity_type}: {ref.label || ref.entity_id}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskDetail;
